import PropTypes from 'prop-types';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import { action, extendObservable } from 'mobx';
import { PolicyModalSectionProps } from '../PolicyModalStore';
import FuseSelectStore from '../../FuseSelectStore';
import FlagStore from '../../FlagStore';
import AsyncActionStore from '../../AsyncActionStore';
import debug, { DEBUG } from '../../../debug';

const DEFAULT_VALUES = {
    matchAllFileTypes: false,
    matchFileTypes: { items: [] },
};

const logger = debug.create('FileTypesStore', DEBUG);

export default class FileTypesStore {
    static PropType = PropTypes.shape({
        fileTypesSelect: FuseSelectStore.PropType.isRequired,
        matchAllFileTypes: FlagStore.PropType.isRequired,
        supportedFileTypes: MobxPropTypes.arrayOrObservableArray,

        ...PolicyModalSectionProps,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class FileTypesStore */{
            fileTypesSelect: new FuseSelectStore({
                filterFn: (option, filter) => {
                    return option.label.toLowerCase().includes(filter)
                        || option.description && option.description.toLowerCase().includes(filter)
                        || option.alias && option.alias.some((a) => a.toLowerCase().includes(filter));
                }
            }),
            matchAllFileTypes: new FlagStore(),
            apiGetFileTypes: null,
            apiGetFileTypesStatus: new AsyncActionStore(),
            originalValue: {},
            originalTypesList: [],
            supportedFileTypes: [],
        }, data);
    }

    setValue = action((value) => {
        value = { ...DEFAULT_VALUES, ...value };
        const items = value.matchFileTypes?.items || [];
        this.originalValue = {
            matchAllFileTypes: value.matchAllFileTypes,
            matchFileTypes: { items }
        };
        this.matchAllFileTypes.setValue(value.matchAllFileTypes);
        this.fileTypesSelect.setSelectedValues([...items]);
    });

    preload = () => {
        if (!this.apiGetFileTypesStatus.isLoading) {
            this.apiGetFileTypesStatus.setLoading(this.apiGetFileTypes().then(action((res) => {
                try {
                    this.originalTypesList = res;
                    const options = res.map((t) => ({
                        value: t.type,
                        label: t.extensions ? `${t.type} (${t.extensions.join(',')})` : t.type,
                        description: t.description,
                        alias: t.alias,
                        common: !!t.common,
                        defaultExtension: t.defaultExtension
                    }));
                    this.fileTypesSelect.setOptions(options);
                } catch (e) {
                    logger.error(e.message);
                }
            })));
        }
        return this.apiGetFileTypesStatus.promise;
    }

    isEmpty = () => !this.matchAllFileTypes.getValue() && this.fileTypesSelect.selectedValues.length === 0;

    getValue = () => ({
        matchAllFileTypes: this.matchAllFileTypes.getValue(),
        matchFileTypes: { items: this.fileTypesSelect.selectedValues },
    });

    clear = () => {
        this.matchAllFileTypes.setValue(DEFAULT_VALUES.matchAllFileTypes);
        this.fileTypesSelect.setSelectedValues(DEFAULT_VALUES.matchFileTypes.items);
    }

    hasChanged = () => {
        const cur = this.getValue();
        return cur.matchAllFileTypes !== this.originalValue.matchAllFileTypes ||
            FuseSelectStore.haveDifferentFlatItems(this.originalValue.matchFileTypes.items, cur.matchFileTypes.items);
    }
}
